import { memo, useState } from 'react';
import { traceStep, traceWhat } from '../engine/localize';
import type { CompileResult, Row, RowSizeResult, TraceStep } from '../engine/types';
import { useI18n } from '../i18n';
import { cellStatus, cellTone, readByIsland, rowStatus, rowTone, toneNoteKey } from '../lib/rowStatus';
import { sizeName } from '../lib/sizes';
import { SeverityIcon } from '../ui/Icon';
import { CanonicalText } from './CanonicalTab';
import { RowLabel } from './RowLabel';
import { StatusLegend, StatusSummary, TONE_STYLE, ToneGlyph } from './RowStatus';

interface Props {
  result: CompileResult;
  selectedSize: number | null;
  activeLine: number | null;
  onJump: (line: number) => void;
}

export const RowsTab = memo(function RowsTab({ result, selectedSize, activeLine, onJump }: Props) {
  const { t, lang } = useI18n();
  const [showCanonical, setShowCanonical] = useState(false);
  const sizes = result.document.sizes;

  const trace = result.module_trace ?? [];
  if (result.rows.length === 0 && trace.length === 0) {
    return <p className="p-6 text-center text-sm text-muted">{t('rows.empty')}</p>;
  }
  if (result.rows.length === 0) {
    return <TraceTable result={result} trace={trace} selectedSize={selectedSize} activeLine={activeLine} onJump={onJump} />;
  }

  const colCls = (index: number) => (selectedSize === index ? 'bg-accent/10' : '');

  return (
    <div className="flex min-h-full flex-col">
      {trace.length > 0 && (
        <div className="border-b border-line">
          <TraceTable result={result} trace={trace} selectedSize={selectedSize} activeLine={activeLine} onJump={onJump} />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-line px-4 py-2 text-2xs text-muted">
        <span className="min-w-[14rem] flex-1">{t('rows.caption')}</span>
        <StatusSummary result={result} selectedSize={selectedSize} />
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-fg">
          <input type="checkbox" className="accent-[rgb(var(--c-accent))]" checked={showCanonical} onChange={(e) => setShowCanonical(e.target.checked)} />
          {t('rows.showCanonical')}
        </label>
      </div>
      <table className="w-full border-separate border-spacing-0 text-xs" data-testid="rows-table">
        <thead className="sticky top-0 z-10 bg-sunken">
          <tr className="text-left text-2xs uppercase tracking-[0.08em] text-subtle">
            <th scope="col" className="w-12 border-b border-line px-2 py-1.5 font-semibold">
              {t('rows.line')}
            </th>
            <th scope="col" className="border-b border-line px-2 py-1.5 font-semibold">
              {t('rows.label')}
            </th>
            {sizes.map((s) => (
              <th
                key={s.index}
                scope="col"
                data-testid="size-column"
                aria-current={selectedSize === s.index ? 'true' : undefined}
                className={`border-b border-line px-2 py-1.5 text-right font-semibold normal-case tracking-normal ${colCls(s.index)} ${
                  selectedSize === s.index ? 'text-accent-strong' : 'text-muted'
                }`}
              >
                {sizeName(result, s.index, lang)}
              </th>
            ))}
            {showCanonical && (
              <th scope="col" className="border-b border-line px-2 py-1.5 font-semibold">
                {t('rows.canonical')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row) => (
            <RowLine
              key={`${row.line_no}-${row.label}`}
              row={row}
              result={result}
              selectedSize={selectedSize}
              active={row.line_no === activeLine}
              showCanonical={showCanonical}
              onJump={onJump}
            />
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-sunken font-bold">
            <td className="border-t border-line px-2 py-1.5" />
            <th scope="row" className="border-t border-line px-2 py-1.5 text-left text-2xs uppercase tracking-wide text-subtle">
              {t('rows.final')}
            </th>
            {sizes.map((s) => (
              <td key={s.index} className={`border-t border-line px-2 py-1.5 text-right tabular ${colCls(s.index)}`}>
                {result.summary.final_counts[s.index] ?? '?'}
              </td>
            ))}
            {showCanonical && <td className="border-t border-line" />}
          </tr>
        </tfoot>
      </table>
      <div className="mt-auto space-y-1 px-3 py-2">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-2xs text-subtle">
          <span className="inline-flex items-center gap-1">
            <SeverityIcon severity="error" size={10} /> {t('rows.hasError')}
          </span>
          <span className="inline-flex items-center gap-1">
            <SeverityIcon severity="warning" size={10} /> {t('rows.hasWarning')}
          </span>
          <span>{t('rows.legendResynced')}</span>
          <span>{t('rows.legendNa')}</span>
        </div>
        <StatusLegend result={result} />
      </div>
    </div>
  );
});

const RowLine = memo(function RowLine({
  row,
  result,
  selectedSize,
  active,
  showCanonical,
  onJump,
}: {
  row: Row;
  result: CompileResult;
  selectedSize: number | null;
  active: boolean;
  showCanonical: boolean;
  onJump: (line: number) => void;
}) {
  const { t, lang } = useI18n();
  const bySize = new Map(row.per_size.map((p) => [p.size, p]));
  return (
    <tr
      onClick={() => onJump(row.line_no)}
      data-line={row.line_no}
      data-status={rowStatus(row, selectedSize) ?? undefined}
      data-tone={rowTone(row, selectedSize) ?? undefined}
      className={`group cursor-pointer ${active ? 'bg-accent/[0.09]' : 'even:bg-sunken/45 hover:bg-accent/[0.05]'}`}
    >
      <td className={`border-b border-line/70 px-2 py-1 ${active ? 'shadow-[inset_3px_0_rgb(var(--c-accent))]' : ''}`}>
        <button
          type="button"
          className="rounded px-1 font-mono text-2xs text-subtle group-hover:text-fg"
          aria-label={t('diag.goto', { n: row.line_no })}
          onClick={(e) => {
            e.stopPropagation();
            onJump(row.line_no);
          }}
        >
          {row.line_no}
        </button>
      </td>
      <td className="border-b border-line/70 px-2 py-1 font-medium">
        <span className="inline-flex items-center gap-1">
          <RowLabel lineNo={row.line_no} label={row.label} result={result} />
          {!row.understood && (
            <span className="rounded-pill bg-warning/15 px-1.5 text-2xs font-bold text-warning" title={t('rows.notUnderstood')}>
              ?<span className="sr-only">{t('rows.notUnderstood')}</span>
            </span>
          )}
        </span>
      </td>
      {result.document.sizes.map((s) => (
        <CountCell key={s.index} row={row} ps={bySize.get(s.index)} sizeLabel={sizeName(result, s.index, lang)} highlighted={selectedSize === s.index} />
      ))}
      {showCanonical && (
        <td className="max-w-[22rem] border-b border-line/70 px-2 py-1 font-mono text-2xs text-muted" lang="en">
          <span className="line-clamp-2">
            <CanonicalText text={row.canonical} patternLang={result.document.lang} />
          </span>
        </td>
      )}
    </tr>
  );
});

/**
 * One count for one size. Error and warning tones come first; otherwise the reading tone tells what the count is
 * worth (ADR-013, decision 6): a verified count in lichen, « ~ » in honey for a count taken on trust, « ≈ » in honey
 * for a row read by island and not confirmed, « ◇ » in amber for an unread row with proposals, « ? » in light wood
 * for a row that was not read. The tooltip says why.
 */
function CountCell({ row, ps, sizeLabel, highlighted }: { row: Row; ps: RowSizeResult | undefined; sizeLabel: string; highlighted: boolean }) {
  const { t } = useI18n();
  const hl = highlighted ? 'bg-accent/10' : '';
  const base = 'border-b border-line/70 px-2 py-1 text-right tabular';
  if (!ps) return <td className={`${base} ${hl} text-subtle`}>·</td>;
  if (!ps.applies) {
    return (
      <td className={`${base} ${hl} italic text-subtle`} title={t('rows.naTitle', { size: sizeLabel })} data-state="na">
        —
      </td>
    );
  }
  const status = cellStatus(ps);
  const tone = cellTone(row, ps);
  const noteKey = toneNoteKey(tone, readByIsland(row));
  const notes: string[] = [];
  if (ps.has_error) notes.push(t('rows.hasError'));
  if (ps.has_warning) notes.push(t('rows.hasWarning'));
  if (ps.resynced) notes.push(t('rows.resynced'));
  if (noteKey) notes.push(t(noteKey));
  const title = [
    t('rows.cellTitle', { size: sizeLabel, available: ps.available ?? '?', after: ps.count_after ?? '?' }),
    ...notes,
  ].join(' · ');
  // The selected column keeps its highlight instead of the tone's own tint.
  const toneCls = tone ? `${TONE_STYLE[tone].cell} ${highlighted ? hl : TONE_STYLE[tone].tint}` : hl;
  const cls = ps.has_error ? 'bg-error/[0.10] font-bold text-error' : ps.has_warning ? 'bg-warning/[0.12] font-semibold text-warning' : toneCls;
  // An unread row with no count shows its « ? » (or « ◇ ») glyph alone, not twice.
  const count = ps.count_after ?? (status === 'unread' ? '' : '?');
  return (
    <td
      className={`${base} ${cls}`}
      title={title}
      data-state={ps.has_error ? 'error' : ps.has_warning ? 'warning' : 'ok'}
      data-status={status ?? undefined}
      data-tone={tone ?? undefined}
    >
      <span className="inline-flex items-center justify-end gap-1">
        {ps.has_error ? <SeverityIcon severity="error" size={10} /> : ps.has_warning ? <SeverityIcon severity="warning" size={10} /> : null}
        <ToneGlyph tone={tone} />
        <span className={ps.resynced ? 'underline decoration-dotted decoration-2 underline-offset-[3px]' : ''}>{count}</span>
      </span>
      {notes.length > 0 && <span className="sr-only"> ({notes.join(', ')})</span>}
    </td>
  );
}

/** Steps verified by a dedicated module (garment schedules, short-row heels): one value per size. */
export function TraceTable({
  result,
  trace,
  selectedSize,
  activeLine,
  onJump,
}: {
  result: CompileResult;
  trace: TraceStep[];
  selectedSize: number | null;
  activeLine: number | null;
  onJump: (line: number) => void;
}) {
  const { t, lang } = useI18n();
  const sizes = result.document.sizes;
  const hl = (i: number) => (selectedSize === i ? 'bg-accent/10' : '');
  return (
    <section aria-label={t('trace.title')} data-testid="trace-section">
      <div className="border-b border-line px-4 py-3">
        <h3 className="font-display text-md font-[500] text-fg">{t('trace.title')}</h3>
        <p className="text-2xs text-muted">{t('trace.caption')}</p>
      </div>
      <table className="w-full border-separate border-spacing-0 text-xs" data-testid="trace-table">
        <thead className="bg-sunken text-left text-2xs uppercase tracking-[0.08em] text-subtle">
          <tr>
            <th scope="col" className="w-12 border-b border-line px-2 py-1.5 font-semibold">{t('rows.line')}</th>
            <th scope="col" className="border-b border-line px-2 py-1.5 font-semibold">{t('trace.what')}</th>
            <th scope="col" className="border-b border-line px-2 py-1.5 font-semibold">{t('trace.step')}</th>
            {sizes.map((s) => (
              <th
                key={s.index}
                scope="col"
                data-testid="trace-size-column"
                aria-current={selectedSize === s.index ? 'true' : undefined}
                className={`border-b border-line px-2 py-1.5 text-right font-semibold normal-case tracking-normal ${hl(s.index)} ${
                  selectedSize === s.index ? 'text-accent-strong' : 'text-muted'
                }`}
              >
                {sizeName(result, s.index, lang)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trace.map((step, i) => (
            <tr
              key={`${step.line_no}-${i}`}
              onClick={() => onJump(step.line_no)}
              data-line={step.line_no}
              className={`group cursor-pointer ${step.line_no === activeLine ? 'bg-accent/[0.09]' : 'even:bg-sunken/45 hover:bg-accent/[0.05]'}`}
            >
              <td className="border-b border-line/70 px-2 py-1">
                <button
                  type="button"
                  className="rounded px-1 font-mono text-2xs text-subtle group-hover:text-fg"
                  aria-label={t('diag.goto', { n: step.line_no })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onJump(step.line_no);
                  }}
                >
                  {step.line_no}
                </button>
              </td>
              <td className="border-b border-line/70 px-2 py-1 font-medium">{traceWhat(step, lang).text}</td>
              <td className="border-b border-line/70 px-2 py-1 text-muted">{traceStep(step, lang).text}</td>
              {sizes.map((s) => {
                const v = step.values[s.index];
                return (
                  <td key={s.index} className={`border-b border-line/70 px-2 py-1 text-right tabular ${hl(s.index)} ${v === null || v === undefined ? 'text-subtle' : ''}`}>
                    {v === null || v === undefined ? '—' : String(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
